import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Breadcrumb.module.css';

export interface BreadcrumbItem {
  readonly label: string;
  /** Assente sull'ultima voce: è la pagina corrente, non un link. */
  readonly to?: string;
}

export interface BreadcrumbProps {
  readonly items: readonly BreadcrumbItem[];
  /**
   * `chiaro` — su fondo bianco/crema (testo grigio-navy);
   * `navy`   — dentro la testata navy delle pagine di sezione (testo chiaro).
   * Stessa struttura, stessa semantica: cambia solo la tavolozza.
   */
  readonly variant?: 'chiaro' | 'navy';
  readonly className?: string;
}

/**
 * Breadcrumb del riferimento (`nav.ic-breadcrumb`, visual-diff pagine
 * 31/07/2026): mono 11px maiuscolo spaziato, separatore «›».
 *
 * Semantica WCAG: `nav` con nome, lista ordinata, ultima voce con
 * `aria-current="page"` e MAI resa come link.
 */
export function Breadcrumb({ items, variant = 'chiaro', className }: BreadcrumbProps) {
  const { t } = useTranslation();
  if (items.length === 0) return null;

  return (
    <nav
      aria-label={t('breadcrumb_aria', 'Percorso di navigazione')}
      className={[styles.nav, variant === 'navy' ? styles.navy : null, className]
        .filter(Boolean)
        .join(' ')}
    >
      <ol className={styles.list}>
        {items.map((item, indice) => {
          const ultima = indice === items.length - 1;
          return (
            <li
              key={`${item.label}-${indice}`}
              className={styles.item}
              {...(ultima ? { 'aria-current': 'page' as const } : {})}
            >
              {indice > 0 ? (
                <span aria-hidden="true" className={styles.separator}>
                  ›
                </span>
              ) : null}
              {item.to && !ultima ? (
                <Link to={item.to} className={styles.link}>
                  {item.label}
                </Link>
              ) : (
                <span className={styles.current}>{item.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
